import React, { useEffect, useRef } from 'react';
import { Bootstrap } from '@ynomia/core/dist/blueprint';
import { useAuth0 } from '@auth0/auth0-react';
import { createSearchParams } from 'react-router-dom';
import {
  ACCESS_TOKEN_STORAGE_KEY, PROJECT_SLUG, REDIRECT_TO, SIGN_IN_ERROR, SIGN_IN_INCOMPLETE,
  USER_UNKNOWN,
} from '../../config/constants';
import {
  analytics, authService, bootstrap, cacheObserver, client, getDecoratedPath,
  getDefaultRoute,
} from '../../services';
import { generateProjectSlug } from '../../utils/url';
import { getContextStores } from '../../context';
import { notification } from '../../antdProvider';

const Auth0CallbackView: React.FC = () => {
  /* Context */
  const { clientCacheDispatch } = getContextStores();

  const { handleRedirectCallback } = useAuth0();
  // React strict mode runs everything twice, prevent this for this page
  const initialLoad = useRef(true);

  const processLogin = async () => {
    const params = new URLSearchParams(window.location.search);
    const hasCallbackParams = params.has('state') && (params.has('code') || params.has('error'));

    // Talk to Auth0 first to check if the login was successful
    let redirectCallback: Awaited<ReturnType<typeof handleRedirectCallback>>;
    try {
      redirectCallback = await handleRedirectCallback();
    } catch (e: any) {
      // A session already stored by this or another app is never cleared from here: this
      // sign-in failing says nothing about it, and signing out would end it for every app on
      // this origin. The user carries on with it, told why if Auth0 refused the sign-in.
      if (localStorage.getItem(ACCESS_TOKEN_STORAGE_KEY)) {
        const failed = hasCallbackParams && e?.error !== 'missing_transaction';
        // Only an Auth0 error response carries the sign-in's saved state, and with it the page
        // the user asked for
        const redirectTo = e?.appState?.[REDIRECT_TO];
        const query = createSearchParams({
          ...(failed ? { [SIGN_IN_ERROR]: e?.error || 'unknown' } : {}),
          ...(typeof redirectTo === 'string' && redirectTo ? { [REDIRECT_TO]: redirectTo } : {}),
        }).toString();
        window.location.replace(query ? `/?${query}` : '/');
        return;
      }
      if (!hasCallbackParams || e?.error === 'missing_transaction') {
        // Not a failed login: a callback URL visited again (reload, back button), without its
        // parameters, or for a sign-in this tab has no record of (started in another tab, or
        // storage cleared mid-flow).
        window.location.replace(`/login?unauthenticatedReason=${SIGN_IN_INCOMPLETE}`);
        return;
      }
      notification.error({
        message: e.error,
        description: e.message,
      });
      client.logout(USER_UNKNOWN);
      return;
    }

    client.configureBaseURL();
    cacheObserver.initialize({ onChange$: client.cache.onChange$ }, clientCacheDispatch);
    await authService.login();

    // Track successful logins
    analytics.trackEvent('User Login');

    let projects: Bootstrap['projects'] | undefined;
    let project: Bootstrap['project'] | undefined;
    try {
      ({ projects, project } = await bootstrap());
    } catch (e: any) {
      // Internal backend issue. Since we have only just signed in, take them back to
      // the login screen.
      client.logout();
      return;
    }

    if (!projects?.length) {
      notification.error({
        message: 'No projects to show!',
      });
      client.logout();
      return;
    }

    const redirectTo = redirectCallback?.appState?.[REDIRECT_TO] as string | undefined;
    let result = getDefaultRoute(redirectTo);
    if (result === '/login') {
      client.logout();
      return;
    }

    if (project) {
      const projectSlug = generateProjectSlug(
        project.metadata.name,
        project.metadata.id,
      );
      if (projectSlug && result.includes(PROJECT_SLUG)) {
        result = getDecoratedPath({ projectSlug }, result);
      }
    }

    // Replace, so the used callback URL is not left in the history for the back button.
    window.location.replace(result);
  };

  useEffect(() => {
    if (initialLoad.current) {
      initialLoad.current = false;
      processLogin().catch(() => {
        // Auth0 accepted the sign-in but finishing it failed, e.g. on an unusable deep link.
        // Never sign out from here: that would end the session of every app on this origin.
        window.location.replace(
          localStorage.getItem(ACCESS_TOKEN_STORAGE_KEY)
            ? '/'
            : `/login?unauthenticatedReason=${SIGN_IN_INCOMPLETE}`,
        );
      });
    }
  }, []);

  return (
    null
  );
};

export default Auth0CallbackView;
